import { useTranslations, Trans } from '@aikoa/i18n/react'
import { useCallback, useMemo, useState } from 'react'
import { View } from 'react-native'
import Animated, { LinearTransition } from 'react-native-reanimated'
import { useDebounce } from 'use-debounce'
import z from 'zod/v4'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper.tsx'
import { useAlert } from '~/components/GlobalAlert'
import Icon from '~/components/Icon.tsx'
import { ViewTitle } from '~/components/layout/Sections.tsx'
import { Body, Label } from '~/components/typography/Content.tsx'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { UrlPreview } from '~/components/UrlPreview'
import { isUrl } from '~/lib/utils.ts'

export const urlInputSchema = z.object({
  url: z.string(),
})

export type UrlInputData = z.infer<typeof urlInputSchema>

export function UrlInputPhase({
  urlData,
  onUpdateData,
  disabled,
}: {
  urlData?: UrlInputData
  onUpdateData: (data: UrlInputData) => unknown
  disabled?: boolean
}) {
  const [currentUrl, setCurrentUrl] = useState(urlData?.url ?? '')
  const [isValidated, setIsValidated] = useState(false)
  const [previewError, setPreviewError] = useState<
    'INVALID_URL' | 'NO_IMAGE' | 'URL_ERROR' | undefined
  >(undefined)
  const { showAlert } = useAlert()
  const { t } = useTranslations('property')

  const isValidUrl = useMemo(() => isUrl(currentUrl), [currentUrl])

  const handleUrlLoad = useCallback(() => {
    setPreviewError(undefined)
    setIsValidated(true)
    onUpdateData({ url: currentUrl })
  }, [currentUrl, onUpdateData])

  const handleUrlError = useCallback(
    (error: 'INVALID_URL' | 'NO_IMAGE' | 'URL_ERROR') => {
      setPreviewError(error)
      setIsValidated(false)

      if (!currentUrl) return

      if (error === 'INVALID_URL') {
        showAlert({
          type: 'error',
          title: t(($) => $.flow.urlInput.error.invalidUrl.title),
          description: t(($) => $.flow.urlInput.error.invalidUrl.description),
        })
        return
      }

      if (error === 'NO_IMAGE') {
        return
      }

      showAlert({
        type: 'error',
        title: t(($) => $.flow.urlInput.error.loadFailed.title),
        description: t(($) => $.flow.urlInput.error.loadFailed.description),
      })
    },
    [currentUrl, showAlert, t]
  )

  const handleUrlChange = useCallback((text: string) => {
    setCurrentUrl(text)
    setIsValidated(false)
  }, [])

  const [debouncedPreviewUrl] = useDebounce(currentUrl, 500, {
    leading: false,
    trailing: true,
  })

  return (
    <PhaseWrapper>
      <ViewTitle>{t(($) => $.flow.urlInput.title)}</ViewTitle>
      <Animated.View layout={LinearTransition.duration(150)} className="flex-1">
        <Animated.View key="url-input" className="gap-6">
          {!!debouncedPreviewUrl && (
            <UrlPreview
              url={debouncedPreviewUrl}
              onLoad={handleUrlLoad}
              onError={handleUrlError}
              className="native:shadow-card items-center justify-center overflow-hidden rounded-lg border border-primary/50 bg-muted text-secondary web:shadow-md web:shadow-primary/50"
            />
          )}

          <View className="flex-initial gap-4">
            <View className="gap-2">
              <Label>{t(($) => $.flow.urlInput.label)}</Label>
              <Input
                placeholder={t(($) => $.flow.urlInput.placeholder)}
                value={currentUrl}
                onChangeText={handleUrlChange}
                autoCapitalize="none"
                keyboardType="url"
                variant="box"
                autoCorrect={false}
                editable={!disabled}
              />
            </View>
            <Body className="text-secondary">
              <Trans
                t={t}
                i18nKey={($) => $.flow.urlInput.description.finland}
                components={{ strong: <Text className="font-medium" /> }}
              />
            </Body>

            {!isValidUrl && currentUrl.length > 0 && (
              <Body className="text-neutral-500">{t(($) => $.flow.urlInput.invalidUrl)}</Body>
            )}
            {!!isValidUrl && !isValidated && !previewError && (
              <Body className="text-neutral-500">{t(($) => $.flow.urlInput.validating)}</Body>
            )}
            {!!isValidated && (
              <View className="flex-row items-center gap-2">
                <Icon name="check" className="text-secondary" size={16} />
                <Body className="text-secondary">{t(($) => $.flow.urlInput.valid)}</Body>
              </View>
            )}
          </View>
        </Animated.View>
      </Animated.View>
    </PhaseWrapper>
  )
}
